// Phase A: privacy-downsampled hover observation (design §6).
//
// A hover is recorded only as "this element was looked at for a while", never as
// a pointer trace: no coordinates, no movement, one bucketed dwell per sample.
//   * pointer devices only (the caller drops touch);
//   * the pointer stayed on the same element for >= minDwellMs;
//   * the same element at most once per perElementCooldownMs;
//   * at most maxPerMinute samples in any rolling minute.
// Hover never triggers support: sanitizeUiActions still drops hover-sourced UI
// actions. This is observation only.

export const HOVER_MIN_DWELL_MS = 600;
export const HOVER_PER_ELEMENT_COOLDOWN_MS = 10000;
export const HOVER_MAX_PER_MINUTE = 20;

export const dwellBucket = (dwellMs) => {
  if (dwellMs < 1500) return '0.6-1.5s';
  if (dwellMs < 3000) return '1.5-3s';
  return '3s+';
};

export const createHoverSampler = ({
  minDwellMs = HOVER_MIN_DWELL_MS,
  perElementCooldownMs = HOVER_PER_ELEMENT_COOLDOWN_MS,
  maxPerMinute = HOVER_MAX_PER_MINUTE,
  now = () => Date.now(),
} = {}) => {
  const lastByElement = new Map();
  let recent = [];

  return {
    // Returns { dwell_bucket } when this dwell should be recorded, else null.
    offer(elementId, dwellMs) {
      if (!elementId || typeof dwellMs !== 'number' || !Number.isFinite(dwellMs)) return null;
      if (dwellMs < minDwellMs) return null;
      const t = now();
      const last = lastByElement.get(elementId);
      if (last != null && t - last < perElementCooldownMs) return null;
      recent = recent.filter((ts) => t - ts < 60000);
      if (recent.length >= maxPerMinute) return null;
      lastByElement.set(elementId, t);
      recent.push(t);
      return { dwell_bucket: dwellBucket(dwellMs) };
    },
    reset() {
      lastByElement.clear();
      recent = [];
    },
  };
};

// Samplers per (student, task), kept at module level: the task view is unmounted while the
// dashboard loads another lesson, so a sampler held by the component would start over when
// the student returns — and "one per element per 10 s" / "20 per task per minute" must hold
// across leaving and returning (supervisor review 2026-10-08).
const taskSamplers = new Map();
const MAX_TASK_SAMPLERS = 200; // a session never visits this many tasks; bounds memory anyway

export const hoverSamplerForTask = (scopeKey) => {
  const key = String(scopeKey || '');
  let sampler = taskSamplers.get(key);
  if (!sampler) {
    if (taskSamplers.size >= MAX_TASK_SAMPLERS) {
      taskSamplers.delete(taskSamplers.keys().next().value); // drop the oldest
    }
    sampler = createHoverSampler();
    taskSamplers.set(key, sampler);
  }
  return sampler;
};

// Tests only.
export const resetTaskHoverSamplers = () => taskSamplers.clear();
